/**
 * ReverseLedgerEntry — BM-H4699 (24 Oct 2026).
 *
 * The correction button for a client ledger row. A row is never deleted
 * (BM-H4691); it is corrected by a reversing entry with a reason, through
 * POST /money-on-account/{id}/reverse. Used on the matter's MoA history and
 * on the Client Account cash book.
 */
import { useState } from "react";
import axios from "axios";
import { Undo2, Check, X } from "lucide-react";
import { API } from "../config/api";

const inputCls = "px-2 py-1 rounded bg-navy-hover border border-navy-border text-white text-xs focus:outline-none focus:border-indigo-400";

function _headers() {
  const email = sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "";
  return {
    "X-Bonsai-User-Id": sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || email,
    "X-User-Email": email,
  };
}

function _err(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  return fallback;
}

export default function ReverseControl({ entryId, onDone, testPrefix = "reverse" }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/money-on-account/${encodeURIComponent(entryId)}/reverse`, { reason }, { headers: _headers() });
      setOpen(false);
      setReason("");
      onDone && onDone();
    } catch (e) {
      setError(_err(e, "Couldn't post the correction"));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-gray-400 hover:text-white" title="Correct with a reversing entry" aria-label="Reverse" data-testid={`${testPrefix}-${entryId}`}>
        <Undo2 className="w-3.5 h-3.5" />
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" className={`${inputCls} w-36`} data-testid={`${testPrefix}-reason-${entryId}`} />
      <button onClick={submit} disabled={busy || reason.trim().length < 3} className="text-indigo-200 hover:text-white disabled:opacity-40" aria-label="Post correction" data-testid={`${testPrefix}-confirm-${entryId}`}>
        <Check className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => { setOpen(false); setError(""); }} className="text-gray-400 hover:text-white" aria-label="Cancel"><X className="w-3.5 h-3.5" /></button>
      {error && <span className="text-[10px] text-status-red">{error}</span>}
    </span>
  );
}
